// @ts-ignore - react-plotly.js doesn't have proper TypeScript types
import { useMemo } from 'react'
import Plot from 'react-plotly.js'
import { AnnotationStatistics, AnnotationConsistencyMetrics } from '../../types'

interface AnalysisChartsProps {
  statistics: AnnotationStatistics
  annotatorCount: number
  analysisMode?: 'sequential' | 'independent'
  consistencyMetrics?: AnnotationConsistencyMetrics[]
  onNavigateToImage?: (imageId: string) => void
}

export default function AnalysisCharts({
  statistics,
  annotatorCount,
  analysisMode = 'sequential',
  consistencyMetrics = [],
  onNavigateToImage,
}: AnalysisChartsProps) {
  // Convert keypoint visibility stats to array for display
  const keypointVisibilityData = statistics.keypoint_visibility_stats
    ? Object.entries(statistics.keypoint_visibility_stats).map(([index, count]) => ({
        index: parseInt(index),
        count,
      }))
    : []

  // Calculate per-image statistics for independent mode
  const imageStats = useMemo(() => {
    if (analysisMode !== 'independent') return null

    const imageData: { [key: string]: { objects: Set<number>; maxObjectsInImage: number } } = {}

    // Group metrics by image
    consistencyMetrics.forEach(metric => {
      if (!imageData[metric.image_id]) {
        imageData[metric.image_id] = {
          objects: new Set<number>(),
          maxObjectsInImage: metric.total_objects_in_image || 1,
        }
      }
      // Use object_index to count unique objects
      imageData[metric.image_id].objects.add(metric.object_index || 0)
      // Update max objects if we have a higher count
      if (
        metric.total_objects_in_image &&
        metric.total_objects_in_image > imageData[metric.image_id].maxObjectsInImage
      ) {
        imageData[metric.image_id].maxObjectsInImage = metric.total_objects_in_image
      }
    })

    // Calculate statistics
    const images = Object.entries(imageData)
    const objectCounts = images.map(([_, data]) => data.maxObjectsInImage)

    // For keypoints, use the mean keypoints per image multiplied by number of objects
    // This is still an estimate but more accurate than before
    const keypointCounts = images.map(([_, data]) => {
      const objectCount = data.maxObjectsInImage
      // Assume each object has roughly the mean number of keypoints
      return Math.round((statistics.mean_keypoints_per_image || 0) * objectCount)
    })

    // Create histogram data
    const objectHistogram: { [key: number]: number } = {}
    const keypointHistogram: { [key: number]: number } = {}

    objectCounts.forEach(count => {
      objectHistogram[count] = (objectHistogram[count] || 0) + 1
    })

    keypointCounts.forEach(count => {
      const bucket = Math.floor(count / 5) * 5 // Group by 5s
      keypointHistogram[bucket] = (keypointHistogram[bucket] || 0) + 1
    })

    return {
      imageCount: images.length,
      objectCounts,
      keypointCounts,
      maxObjects: Math.max(...objectCounts, 0),
      maxKeypoints: Math.max(...keypointCounts, 0),
      avgObjectsPerImage:
        objectCounts.length > 0 ? objectCounts.reduce((a, b) => a + b, 0) / objectCounts.length : 0,
      avgKeypointsPerImage:
        keypointCounts.length > 0
          ? keypointCounts.reduce((a, b) => a + b, 0) / keypointCounts.length
          : 0,
      objectHistogram,
      keypointHistogram,
      imageData, // Include the raw image data for the table
    }
  }, [analysisMode, consistencyMetrics, statistics.mean_keypoints_per_image])

  // For independent mode, show different statistics
  if (analysisMode === 'independent' && imageStats) {
    return (
      <div className="space-y-6">
        {/* Per-Image Statistics Summary */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Per-Image Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Total Images</h4>
              <p className="text-2xl font-semibold text-blue-700">{imageStats.imageCount}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-purple-900 mb-2">Avg Objects/Image</h4>
              <p className="text-2xl font-semibold text-purple-700">
                {imageStats.avgObjectsPerImage.toFixed(1)}
              </p>
              <p className="text-xs text-purple-600">Max: {imageStats.maxObjects}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-900 mb-2">Avg Keypoints/Image</h4>
              <p className="text-2xl font-semibold text-green-700">
                {imageStats.avgKeypointsPerImage.toFixed(0)}
              </p>
              <p className="text-xs text-green-600">Max: {imageStats.maxKeypoints}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-amber-900 mb-2">Multi-Object Images</h4>
              <p className="text-2xl font-semibold text-amber-700">
                {imageStats.objectCounts.filter(c => c > 1).length}
              </p>
              <p className="text-xs text-amber-600">
                {imageStats.imageCount > 0
                  ? `${((imageStats.objectCounts.filter(c => c > 1).length / imageStats.imageCount) * 100).toFixed(0)}%`
                  : '0%'}
              </p>
            </div>
          </div>
        </div>

        {/* Objects per Image Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Objects per Image Distribution</h3>
          <Plot
            data={[
              {
                x: imageStats.objectCounts,
                type: 'histogram',
                marker: { color: '#8b5cf6' },
                nbinsx: Math.min(imageStats.maxObjects, 10),
                name: 'Objects per Image',
              },
            ]}
            layout={{
              xaxis: {
                title: 'Number of Objects',
                dtick: 1,
                range: [-0.5, imageStats.maxObjects + 0.5],
              },
              yaxis: { title: 'Number of Images' },
              height: 300,
              margin: { l: 50, r: 30, t: 30, b: 50 },
              showlegend: false,
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              bargap: 0.1,
            }}
            config={{ displayModeBar: false }}
            style={{ width: '100%' }}
          />
        </div>

        {/* Detailed Image Table */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Image Details</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Image ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Objects
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Keypoints (Est.)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(imageStats.imageData)
                  .sort(([_, a], [__, b]) => b.maxObjectsInImage - a.maxObjectsInImage)
                  .map(([imageId, data]) => {
                    const objectCount = data.maxObjectsInImage
                    const keypointCount = Math.round(
                      (statistics.mean_keypoints_per_image || 0) * objectCount
                    )

                    return (
                      <tr key={imageId} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                          {onNavigateToImage ? (
                            <button
                              onClick={() => onNavigateToImage(imageId)}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {imageId.substring(0, 8)}...
                            </button>
                          ) : (
                            <span className="text-gray-900">{imageId.substring(0, 8)}...</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={objectCount > 1 ? 'font-semibold text-purple-600' : ''}>
                            {objectCount}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {keypointCount}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          {objectCount > 1 ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Multi-Object
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Single Object
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  // Original charts for sequential mode
  return (
    <div className="space-y-6">
      {/* Keypoint Statistics */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Keypoint Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Average Keypoints per Image</h4>
            <p className="text-2xl font-semibold text-gray-900">
              {statistics.mean_keypoints_per_image?.toFixed(1) || '0.0'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              ± {statistics.std_keypoints_per_image?.toFixed(1) || '0.0'} std dev
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Average Bbox Size</h4>
            <p className="text-2xl font-semibold text-gray-900">
              {statistics.mean_bbox_size?.toFixed(0) || '0'} px²
            </p>
            <p className="text-sm text-gray-500 mt-1">
              ± {statistics.std_bbox_size?.toFixed(0) || '0'} std dev
            </p>
          </div>
        </div>
      </div>

      {/* Keypoint Visibility Chart */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Keypoint Visibility</h3>
        <div className="bg-gray-50 rounded-lg p-4">
          {keypointVisibilityData.length > 0 ? (
            <div className="space-y-2">
              {keypointVisibilityData.map(kp => (
                <div key={kp.index} className="flex items-center">
                  <span className="text-sm font-medium text-gray-700 w-24">
                    Keypoint {kp.index}:
                  </span>
                  <div className="flex-1 bg-gray-200 rounded-full h-4 ml-2">
                    <div
                      className="bg-blue-600 h-4 rounded-full"
                      style={{
                        width: `${(kp.count / Math.max(...keypointVisibilityData.map(k => k.count))) * 100}%`,
                      }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 ml-2 w-12 text-right">{kp.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No keypoint visibility data available</p>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Analysis Summary</h4>
        <p className="text-sm text-blue-700">
          This project has {annotatorCount} annotator{annotatorCount !== 1 ? 's' : ''} working on
          annotations. The average annotation contains{' '}
          {statistics.mean_keypoints_per_image?.toFixed(1) || '0.0'} visible keypoints with a
          standard deviation of {statistics.std_keypoints_per_image?.toFixed(1) || '0.0'}.
        </p>
      </div>
    </div>
  )
}
